import { InputTypes, SortOrder } from "constants/enums";
import { IFormField } from "models/app";
import React, { useState } from "react";
import Modal from "components/UI/DialogModal";
import Form from "components/UI/Form";
import { CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, TableCell, Tooltip, Typography } from "@mui/material";
import MuiButton from "components/UI/MuiButton";
import { IEmployee } from "models/employee";
import { ICountry } from "models/country";
import { ICompanyJob } from "models/companyJob";
import { useTranslation } from "react-i18next";
import { ICompanyDepartments } from "models/companyDepartment";
import List from "components/UI/List";
import { EmployeeCells, UpdateEmployeeCells } from "framework/employee";
import { Feature } from "models/api";
import { IState } from "components/UI/List/List";
import { useAlert } from "contexts/alertContext";


interface PersonInfo {
  residenceCountryId: string;
  companyJobId: string;
  gender: number | null;
  id: string;
}


interface Props {
  open: boolean;
  loading: boolean;
  importedEmployees: IEmployee[];
  Countries: ICountry[];
  CompanyJobs: ICompanyJob[];
  CompanyDepartments: ICompanyDepartments[];
  feature: Feature;
  onClose: () => void;
  onSubmit: (data:any) => void;
}

const UpdateEmployees: React.FC<Props> = ({
  open,
  loading,
  feature,
  Countries,
  CompanyJobs,
  CompanyDepartments,
  importedEmployees,
  onClose,
  onSubmit,
}) => {
  
  const { t,i18n } = useTranslation();
  const { setAlert } = useAlert();
  const [people, setPeople] = useState<PersonInfo[]>(importedEmployees.map((person)=> {
    return {
      residenceCountryId: '',
      companyJobId: '',
      gender: null,
      id: person.id
    }
  }));

  const [filteredJobs,setFilteredJobs]= useState<{id:string,departmentId:string}[]>([])

  const handleSetFiltered = (id: string, departmentId: string) => {
    setFilteredJobs(prev => {
      const jobExists = prev.some(job => job.id === id);
  
      if (jobExists) {
        return prev.map(job => 
          job.id === id ? { id, departmentId } : job
        );
      } else {
        return [...prev, { id, departmentId }];
      }
    });

    setPeople(prevPeople => 
      prevPeople.map(person => {
        if (person.id === id) {
          return { ...person, companyJobId: '' };
        }
        return person;
      })
    );

  };
  

  const handleChange = (id: string, field: keyof PersonInfo, value: string | number) => {
    setPeople((prevPeople) =>
      prevPeople.map((person) =>
        person.id === id ? { ...person, [field]: value } : person
      )
    );
  };

  const countriesOptions = Countries.map((country) => {
    return { value: country.id, label: country.name };
  });


  const jobsOptions = CompanyJobs.map((job) => ({
      value: job.id,
      label: job.name
    }));


  const departmentsOptions = CompanyDepartments.map((job) => {
    return { value: job.id, label: job.name };
  });

  const genderOptions = [{id:'1',name:'Male'},{id:'2',name:'Female'}].map((job) => {
    return { value: job.id, label: job.name };
  });


  const getBodyCells = (row: IEmployee) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "firstName")
        return {
          id: "FirstName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row?.firstName : row?.firstNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "middleName")
        return {
          id: "MiddleName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row?.middleName : row?.middleNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "lastName")
        return {
          id: "LastName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row?.lastName : row?.lastNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "email")
        return {
          id: "Email",
          element: (
            <TableCell align={"left"} key={i}>
              <Tooltip title={row?.email}>
                <Typography variant="body2">{row?.email}</Typography>
              </Tooltip>
            </TableCell>
          ),
        };

        if (key === "gender")
          return {
            id: "gender",
            element: (
              <TableCell align={"left"} key={i}>
                 <FormControl fullWidth variant="outlined" margin="normal">
        <InputLabel>{t('T_gender')}</InputLabel>
        <Select
          onChange={(e) => handleChange(row.id, 'gender', Number(e.target.value))}
          label={t('T_gender')}
        >
          {genderOptions.map((country) => (
            <MenuItem key={country.value} value={country.value}>
              {country.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
              </TableCell>
            ),
          };


        if (key === "residenceCountryId")
          return {
            id: "residenceCountryId",
            element: (
              <TableCell align={"left"} key={i}>
                 <FormControl fullWidth variant="outlined" margin="normal">
        <InputLabel>{t('T_country')}</InputLabel>
        <Select
          onChange={(e) => handleChange(row.id, 'residenceCountryId', e.target.value as string|number)}
          label={t('T_country')}
        >
          {countriesOptions.map((country) => (
            <MenuItem key={country.value} value={country.value}>
              {country.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
              </TableCell>
            ),
          };

          if (key === "id")
            return {
              id: "Id",
              element: (
                <TableCell align={"left"} key={i}>
                   <FormControl fullWidth variant="outlined" margin="normal">
          <InputLabel>{t('T_department')}</InputLabel>
          <Select
            onChange={(e) => handleSetFiltered(row.id,  e.target.value as string)}
            label={t('T_department')}
          >
            {departmentsOptions.map((job) => (
              <MenuItem key={job.value} value={job.value}>
                {job.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
                </TableCell>
              ),
            };

      
          if (key === "companyJobId")
        return {
          id: "companyJobId",
          element: (
            <TableCell align={"left"} key={i}>
               <FormControl fullWidth variant="outlined" margin="normal">
      <InputLabel>{t('T_job')}</InputLabel>
      <Select
      value={people.find(person=> row.id === person.id)?.companyJobId}
        onChange={(e) => handleChange(row.id, 'companyJobId', e.target.value as string|number)}
        label={t('T_job')}
      >
        {(filteredJobs.find(job=> job.id === row.id)? CompanyJobs.filter((x) => x.companyDepartmentId === filteredJobs.find(job=> job.id === row.id)?.departmentId ).map((job) => ({
      value: job.id,
      label: job.name
    }))  : jobsOptions).map((job) => (
          <MenuItem key={job.value} value={job.value}>
            {job.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
            </TableCell>
          ),
        };

      else return <></>;
    });
    return cells;
  };


  const INITIAL_STATE: IState = {
    order: SortOrder.DESC,
    orderBy: "created_at",
    page: 0,
    rowsPerPage: 10000,
  };

 const validate = () => {
    const newErrors: { [key: string]: { country: boolean; job: boolean; gender: boolean } } = {};
    let isValid = true;

    people.forEach((person) => {
      const hasCountryError = person.residenceCountryId === '';
      const hasJobError = person.companyJobId === '';
      const hasGenderError = person.gender === null;

      if (hasCountryError || hasJobError || hasGenderError) {
        isValid = false;
        newErrors[person.id] = { country: hasCountryError, job: hasJobError, gender: hasGenderError };
      }
    });

    return isValid || true;
  };


  const handleSubmit=()=>{
    if (validate()) {
      onSubmit(people)
    }
    else {
      setAlert({
        open: true,
        message: "Fill all required fields",
        type: "error",
      });
    }

  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title="Edit Employee"
        maxWidth="xl"
        fullWidth
        fullScreen
        children={
          <Stack spacing={2} p={2}>
            <List
            headCells={UpdateEmployeeCells}
            data={importedEmployees}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
            initialState={INITIAL_STATE}
          />
          </Stack>
        }
        actions={
          <Grid
            container
            alignItems={"center"}
            justifyContent={"space-between"}
            px={1}
          >
            <MuiButton
              variant="contained"
              sx={{ bgcolor: "warning.main", color: "white" }}
              size="large"
              onClick={onClose}
            >
              {t("T_cancel")}
            </MuiButton>
            <MuiButton
              variant="contained"
              sx={{ bgcolor: "primary.main", color: "white" }}
              disabled={loading}
              size="large"
              onClick={handleSubmit}
            >
              {t("T_save")}
              <CircularProgress
                size={10}
                sx={{ mx: 2, display: loading ? "" : "none" }}
              />
            </MuiButton>
          </Grid>
        }
      />
    </>
  );
};

export default UpdateEmployees;
